import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TopNav } from '../components/sections/TopNav.tsx';
import { Footer } from '../components/sections/Footer.tsx';
import { CmsHero } from '../components/sections/CmsHero.tsx';
import { CmsSectionRenderer } from '../components/sections/CmsSectionRenderer.tsx';
import { loadPage, loadPreview, filterSectionsByPhase, resolveHero } from '../lib/pages.ts';
import { useFestivalData } from '../lib/festivalData.ts';
import { resolveActivePhase } from '../lib/activePhase.ts';
import { resolvePreviewPhase, PHASE_PREVIEW_DEFAULTS } from '../lib/previewPhaseOverride.ts';
import { usePageMeta } from '../lib/usePageMeta.ts';
import { headingPrefix } from '../../shared/meta.mjs';
import { useTweaks } from '../components/useTweaks.ts';
import { PhasePreviewSwitcher } from '../components/PhasePreviewSwitcher.tsx';
import type { PageData } from '../lib/types.ts';

interface CmsPageProps {
  /** Hardcoded slug for a published page (pages/{slug}.json). */
  readonly slug?: string;
  /** When true, loads preview/{guid}.json using the :guid route param. */
  readonly preview?: boolean;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; page: PageData };

/**
 * Renders a CMS-driven page from JSON. Shows a spinner until loaded, then the
 * hero + ordered sections. Used both for published pages (by slug) and for
 * the dedicated /preview/:guid route.
 */
export default function CmsPage({ slug, preview = false }: CmsPageProps) {
  const { guid } = useParams<{ guid: string }>();
  // The load result together with the key it belongs to. Pairing them lets 'still loading'
  // be derived during render whenever the key moves ahead of the result, instead of the effect
  // resetting state synchronously — which is a cascading render the React Compiler flags.
  const [loaded, setLoaded] = useState<{ key: string; state: LoadState } | null>(null);
  const { data } = useFestivalData();
  const [tweaks, setTweak] = useTweaks(PHASE_PREVIEW_DEFAULTS);

  const key = preview ? `preview:${guid}` : `page:${slug}`;
  const state: LoadState = loaded && loaded.key === key ? loaded.state : { status: 'loading' };

  useEffect(() => {
    let cancelled = false;

    const promise = preview
      ? (guid ? loadPreview(guid) : Promise.reject(new Error('Manglende preview-id')))
      : (slug ? loadPage(slug) : Promise.reject(new Error('Manglende slug')));

    promise
      .then(page => { if (!cancelled) setLoaded({ key, state: { status: 'ready', page } }); })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        if (!cancelled) setLoaded({ key, state: { status: 'error', message } });
      });

    return () => { cancelled = true; };
  // key captures both slug and guid
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Home keeps the pre-CMS-038 "page" nav mode: the hardcoded "Køb billet" pill and any
  // same-page anchor jump smoothly to the target section's id instead of navigating away
  // (CmsSectionShell ids, e.g. CmsTicketsSection's "billetter"), matching the old HomePage.tsx.
  const isHome = slug === 'home';

  // Resolved here rather than after the early returns, because the head has to be updated on every
  // render path and a hook cannot be called conditionally. Null until the page has loaded: the
  // tags the server wrote stay in place until there is something truer to say.
  const readyPage = state.status === 'ready' ? state.page : null;
  const resolvedPhase = resolveActivePhase(data?.program.phases, new Date());
  const activePhase = resolvePreviewPhase(preview, tweaks, resolvedPhase);
  const hero = readyPage ? resolveHero(readyPage.hero, readyPage.phaseVariants, activePhase) : null;

  usePageMeta({
    title: readyPage ? (readyPage.seo?.metaTitle ?? hero?.title) : null,
    description: readyPage?.seo?.metaDescription ?? hero?.subtitle,
    // A preview is an unpublished draft at a guessable address; it has no business being indexed.
    noIndex: preview || readyPage?.seo?.noIndex === true,
    isHome,
  });

  if (state.status === 'loading') {
    return (
      <>
        <TopNav mode="subpage" />
        <div className="min-h-[60vh] flex items-center justify-center bg-mf-yellow" role="status" aria-live="polite">
          <span className="inline-block w-10 h-10 rounded-full border-2 border-mf-green/30 border-t-mf-green animate-spin" />
          <span className="sr-only">Indlæser…</span>
        </div>
        <Footer />
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <TopNav mode="subpage" />
        <div className="min-h-[60vh] flex items-center justify-center bg-mf-yellow p-10 text-center">
          <div>
            <div className="font-display text-3xl text-mf-black mb-2">Noget gik galt</div>
            <div className="text-mf-black/70">{state.message}</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const { page } = state;
  const visibleSections = filterSectionsByPhase(page.sections, activePhase);
  // Resolved above for the head; the page has loaded by here, so it is never the fallback.
  const resolvedHero = hero ?? page.hero;

  const onJump = (id: string): void => {
    if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 64;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <>
      <TopNav mode={isHome ? 'page' : 'subpage'} onJump={isHome ? onJump : undefined} />
      {/* The page's content, as a landmark: the site had none, so "skip to the main content" had
          nothing to skip to, and a screen reader read the navigation on every page. */}
      <main id="indhold">
        <CmsHero {...resolvedHero} headingPrefix={headingPrefix(resolvedHero.title)} />
        {visibleSections.map((section, i) => (
          <CmsSectionRenderer key={i} section={section} />
        ))}
      </main>
      <Footer />
      {preview && <PhasePreviewSwitcher tweaks={tweaks} setTweak={setTweak} />}
    </>
  );
}
